import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/* Brand preset for the Kundenportal: primary from the g&o bordeaux (#B00930),
   surfaces matching the click-dummy's light/dark token sets. */
export const KundenportalPreset = definePreset(Aura, {
  semantic: {
    primary: palette('#B00930'),
    colorScheme: {
      light: {
        primary: {
          color: '#B00930',
          hoverColor: '#8F0726',
          activeColor: '#8F0726',
          contrastColor: '#ffffff'
        },
        surface: {
          0: '#ffffff',
          50: '#fbfbfd',
          100: '#f3f3f6',
          200: '#ededf0',
          300: '#e5e5ea',
          400: '#cfcfd6',
          500: '#8e8e93',
          600: '#6e6e73',
          700: '#3a3a3c',
          800: '#2a2a30',
          900: '#1c1c1e',
          950: '#131316'
        }
      },
      dark: {
        primary: {
          color: '#B00930',
          hoverColor: '#8F0726',
          activeColor: '#8F0726',
          contrastColor: '#ffffff'
        },
        surface: {
          0: '#f2f2f4',
          50: '#d7d7dc',
          100: '#b9b9c0',
          200: '#a0a0a8',
          300: '#83838b',
          400: '#5a5a62',
          500: '#4a4a52',
          600: '#36363c',
          700: '#2b2b30',
          800: '#232328',
          900: '#1d1d21',
          950: '#131316'
        }
      }
    }
  },
  components: {
    button: {
      // Dummy .pt-btn: 11px radius, 18px side padding, 600 weight. Aura derives the radius from
      // the form-field token (6px here), which read as a different design system next to the
      // 14px cards. The absolute box metrics (min-height 42px/36px) are in styles.css.
      root: {
        borderRadius: '11px',
        paddingX: '18px',
        gap: '8px',
        label: { fontWeight: '600' },
        sm: { fontSize: '13.5px', paddingX: '14px' }
      },
      colorScheme: {
        // Outlined secondary (e.g. the broker card's "Details"): ink lettering on a visible grey
        // border — Aura's default is grey-on-near-white, which reads as disabled next to a filled
        // primary button. Same values as --gs-ink / --gs-hair-3 in styles.css.
        light: {
          outlined: {
            secondary: { borderColor: '{surface.400}', color: '{surface.900}' }
          }
        },
        dark: {
          outlined: {
            secondary: { borderColor: '{surface.500}', color: '{surface.0}' }
          }
        }
      }
    },
    dialog: {
      // A modal reads as one of the portal's cards (.gs-card / .gs-card-head): 14px radius, hairline
      // border, 16px bold title. Aura's modal is 12px on surface.200 with 1.25rem padding. The content
      // padding is 0 because the rows inside (.gs-row, group labels) pad themselves — global dialog
      // defaults, but the broker details are the only dialog so far.
      root: { borderRadius: 'var(--gs-radius)' },
      header: { padding: '14px 20px' },
      title: { fontSize: '16px', fontWeight: '700' },
      content: { padding: '0' },
      colorScheme: {
        light: {
          root: { background: '{surface.0}', borderColor: '{surface.300}', color: '{surface.900}' }
        },
        dark: {
          root: { background: '{surface.900}', borderColor: '{surface.600}', color: '{surface.0}' }
        }
      }
    },
    selectbutton: {
      root: { borderRadius: '10px' }
    },
    select: {
      // The Vertragsstatus filter — dummy .va-filter select is a 10px control, not Aura's 6px
      // form-field radius. Height and type weight are in styles.css.
      root: { borderRadius: '10px' }
    },
    togglebutton: {
      // The Privat/Firma switch as a segmented control: grey track, the checked option a white
      // pill (--gs-seg / --gs-seg-on in styles.css). Aura's track is surface.100, which is the page
      // background here, so the control vanished into the page.
      // The track and the checked pill are the two tokens the dummy names explicitly (--seg /
      // --seg-on), so they reference the CSS variables rather than the nearest surface step —
      // Aura's surface.200/500 are 1-2 units off in both themes. checkedShadow is what makes the
      // selected option read as raised; Aura's flat card shadow does not.
      root: { padding: '4px', borderRadius: '10px', fontWeight: '600' },
      content: {
        padding: '6px 28px',
        borderRadius: '8px',
        checkedShadow: '0 1px 3px rgb(0 0 0 / 12%)'
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--gs-seg)',
            checkedBackground: 'var(--gs-seg)',
            hoverBackground: 'var(--gs-seg)',
            borderColor: 'var(--gs-seg)',
            checkedBorderColor: 'var(--gs-seg)',
            color: '{surface.600}',
            hoverColor: '{surface.700}',
            checkedColor: '{surface.900}'
          },
          content: { checkedBackground: 'var(--gs-seg-on)' }
        },
        dark: {
          root: {
            background: 'var(--gs-seg)',
            checkedBackground: 'var(--gs-seg)',
            hoverBackground: 'var(--gs-seg)',
            borderColor: 'var(--gs-seg)',
            checkedBorderColor: 'var(--gs-seg)',
            color: '{surface.200}',
            hoverColor: '{surface.50}',
            checkedColor: '{surface.0}'
          },
          content: { checkedBackground: 'var(--gs-seg-on)' }
        }
      }
    }
  }
});
